import {
  decodeJsonPointerSegment,
  getEvidenceDocumentRoot,
  normalizeJsonPointer,
  resolveJsonPointer,
} from "./reportEvidencePointer";
import { exitPlacementSliceDisplayLabel } from "./exitPlacementDisplay";

export interface TesFocusSliceCatalogRow {
  focus_index?: number;
  slice_id?: string;
  segment_label?: string;
  segment_key?: string;
  situation?: string;
  display_label?: string;
  sample_size?: number;
  [key: string]: unknown;
}

export interface SupportingSegmentDisplay {
  sliceId: string;
  segmentLabel: string;
  sampleSize?: number;
  fromCatalog: boolean;
}

export type TesFocusPointerParse =
  | { mode: "by_slice_id"; sliceId: string; subPath: string }
  | { mode: "index"; index: number; subPath: string };

export type EvidencePointerResolution = {
  value: unknown;
  /** Shown when a legacy numeric index disagrees with `primary_slice_id`. */
  warning?: string;
};

function snapshotRecord(snapshot: unknown): Record<string, unknown> | null {
  const root = getEvidenceDocumentRoot(snapshot);
  if (!root || typeof root !== "object" || Array.isArray(root)) return null;
  return root as Record<string, unknown>;
}

export function getTesFocusSlices(snapshot: unknown): Record<string, unknown>[] {
  const snap = snapshotRecord(snapshot);
  const raw = snap?.tes_focus_slices;
  if (!Array.isArray(raw)) return [];
  return raw.filter((item): item is Record<string, unknown> => Boolean(item) && typeof item === "object");
}

export function getTesFocusSliceCatalog(snapshot: unknown): TesFocusSliceCatalogRow[] {
  const snap = snapshotRecord(snapshot);
  const raw = snap?.tes_focus_slice_catalog;
  if (!Array.isArray(raw)) return [];
  return raw.filter((item): item is TesFocusSliceCatalogRow => Boolean(item) && typeof item === "object");
}

export function findTesFocusSliceById(
  slices: Record<string, unknown>[],
  sliceId: string
): Record<string, unknown> | undefined {
  const id = sliceId.trim();
  if (!id) return undefined;
  return slices.find((row) => String(row.slice_id ?? "").trim() === id);
}

export function parseTesFocusEvidencePointer(pointer: string): TesFocusPointerParse | null {
  const normalized = normalizeJsonPointer(pointer);
  const parts = normalized.split("/").filter(Boolean);
  if (parts[0] !== "tes_focus_slices") return null;

  if (parts[1] === "by_slice_id" && parts[2]) {
    const sliceId = decodeJsonPointerSegment(parts[2]);
    const rest = parts.slice(3).map(decodeJsonPointerSegment);
    return {
      mode: "by_slice_id",
      sliceId,
      subPath: rest.length ? `/${rest.join("/")}` : "",
    };
  }

  if (parts[1] && /^\d+$/.test(parts[1])) {
    const rest = parts.slice(2).map(decodeJsonPointerSegment);
    return {
      mode: "index",
      index: Number(parts[1]),
      subPath: rest.length ? `/${rest.join("/")}` : "",
    };
  }

  return null;
}

const TES_FOCUS_SUBPATH_ALIASES: Record<string, string> = {
  "/exit_placement/display/tp_d": "/exit_placement/display/tp_distance_price",
  "/exit_placement/display/sl_d": "/exit_placement/display/sl_distance_price",
};

function resolveOnTesFocusSliceRow(
  slice: Record<string, unknown> | undefined,
  subPath: string
): unknown {
  if (!slice) return undefined;
  if (!subPath || subPath === "/") return slice;
  const value = resolveJsonPointer(slice, subPath);
  if (value !== undefined) return value;
  const alias = TES_FOCUS_SUBPATH_ALIASES[subPath];
  if (alias) return resolveJsonPointer(slice, alias);
  return undefined;
}

export function resolveTesFocusEvidencePointer(
  snapshot: unknown,
  pointer: string,
  primarySliceId?: string
): EvidencePointerResolution {
  const parsed = parseTesFocusEvidencePointer(pointer);
  if (!parsed) return { value: undefined };

  const slices = getTesFocusSlices(snapshot);
  let warning: string | undefined;

  if (parsed.mode === "by_slice_id") {
    const slice = findTesFocusSliceById(slices, parsed.sliceId);
    return { value: resolveOnTesFocusSliceRow(slice, parsed.subPath) };
  }

  const slice = slices[parsed.index];
  const rowSliceId = slice ? String(slice.slice_id ?? "").trim() : "";
  const primary = (primarySliceId ?? "").trim();
  if (primary && rowSliceId && rowSliceId !== primary) {
    warning = `Legacy pointer index ${parsed.index} refers to “${rowSliceId}”, but this finding’s primary segment is “${primary}”. Prefer /tes_focus_slices/by_slice_id/… paths.`;
  }

  return {
    value: resolveOnTesFocusSliceRow(slice, parsed.subPath),
    warning,
  };
}

export function getSupportingSegmentDisplay(
  snapshot: unknown,
  primarySliceId: string | undefined
): SupportingSegmentDisplay | null {
  const sliceId = (primarySliceId ?? "").trim();
  if (!sliceId) return null;

  const catalog = getTesFocusSliceCatalog(snapshot);
  const catalogRow = catalog.find((row) => String(row.slice_id ?? "").trim() === sliceId);
  if (catalogRow) {
    const label = exitPlacementSliceDisplayLabel(catalogRow);
    return {
      sliceId,
      segmentLabel: label || sliceId,
      sampleSize:
        typeof catalogRow.sample_size === "number" && Number.isFinite(catalogRow.sample_size)
          ? catalogRow.sample_size
          : undefined,
      fromCatalog: true,
    };
  }

  const slice = findTesFocusSliceById(getTesFocusSlices(snapshot), sliceId);
  if (slice) {
    const label = exitPlacementSliceDisplayLabel(slice as Parameters<typeof exitPlacementSliceDisplayLabel>[0]);
    const sample =
      typeof slice.sample_size === "number" && Number.isFinite(slice.sample_size)
        ? slice.sample_size
        : undefined;
    return {
      sliceId,
      segmentLabel: label || sliceId,
      sampleSize: sample,
      fromCatalog: false,
    };
  }

  return {
    sliceId,
    segmentLabel: sliceId,
    fromCatalog: false,
  };
}

export function getEvidencePointerWarnings(snapshot: unknown): string[] {
  const snap = snapshotRecord(snapshot);
  const tesGen = snap?.tes_generation;
  if (!tesGen || typeof tesGen !== "object" || Array.isArray(tesGen)) return [];
  const diagnostics = (tesGen as Record<string, unknown>).diagnostics;
  if (!diagnostics || typeof diagnostics !== "object" || Array.isArray(diagnostics)) return [];
  const raw = (diagnostics as Record<string, unknown>).evidence_pointer_warnings;
  if (!Array.isArray(raw)) return [];
  return raw
    .map((item) => (typeof item === "string" ? item.trim() : ""))
    .filter(Boolean);
}
